import { useEffect, useMemo, useState } from 'react';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { IssuerNotificationTable } from '@/modules/issuer-dashboard/components/IssuerNotificationTable.tsx';
import { useIssuerUpdateStatusDividendEventMutation } from '@/modules/issuer-dashboard/queries/dividends.tsx';
import { useGetIssuersByRegisterQuery } from '@/modules/issuer-dashboard/queries/registers.tsx';
import { BaseModalProps, Button, ModalContentWrapper, SelectOption } from 'ui';

interface IssuerFinalizeDistributionModalProps extends BaseModalProps {
  registerId: number;
  dividendId: number;
}

export const IssuerFinalizeDistributionModal = ({
  open,
  onClose,
  registerId,
  dividendId,
}: IssuerFinalizeDistributionModalProps) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const [notifyIssuers, setNotifyIssuers] = useState(true);
  const [issuerUserIds, setIssuerUserIds] = useState<number[]>([]);
  const [hasInitialisedIssuerRecipients, setHasInitialisedIssuerRecipients] = useState(false);
  const { data: issuersData, isSuccess: issuerRecipientsLoaded } = useGetIssuersByRegisterQuery(
    { id: registerId, page: 1, page_size: 500 },
    !registerId,
  );
  const issuerOptions = useMemo<SelectOption<number>[]>(() => {
    const issuers = (issuersData?.pages || []).flatMap(page => page.data || []);
    return Array.from(new Map(issuers.map(issuer => [issuer.id, issuer])).values()).map(issuer => ({
      value: issuer.id,
      label: `${issuer.first_name} ${issuer.last_name}`.trim() || issuer.email,
    }));
  }, [issuersData?.pages]);

  useEffect(() => {
    if (issuerOptions.length && !hasInitialisedIssuerRecipients) {
      setIssuerUserIds(issuerOptions.map(({ value }) => value));
      setHasInitialisedIssuerRecipients(true);
    }
  }, [hasInitialisedIssuerRecipients, issuerOptions]);
  const { mutateAsync: finalizeDividend, isLoading } = useIssuerUpdateStatusDividendEventMutation({
    onSuccess: onClose,
  });

  return (
    <ModalContentWrapper
      open={open}
      onClose={onClose}
      title={`Finalise ${getRegisterTypeStrings('dividend', { capitalize: false })}`}
    >
      <div className="flex flex-col gap-5">
        <p>Finalise this {getRegisterTypeStrings('dividend', { capitalize: false })} now?</p>
        <IssuerNotificationTable
          issuerOptions={issuerOptions}
          notifyIssuers={notifyIssuers}
          issuerUserIds={issuerUserIds}
          onNotifyIssuersChange={setNotifyIssuers}
          onIssuerUserIdsChange={setIssuerUserIds}
        />
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await finalizeDividend({
                register_id: registerId,
                id: dividendId,
                do_not_notify_issuers: !notifyIssuers,
                issuer_user_ids:
                  notifyIssuers && issuerRecipientsLoaded ? issuerUserIds : undefined,
              });
            }}
            loading={isLoading}
            disabled={isLoading}
          >
            Finalise
          </Button>
        </div>
      </div>
    </ModalContentWrapper>
  );
};
